import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {AppState} from "../../reducers/RootReducer";
import {StateObservable, ofType} from "redux-observable";
import {switchMap, map, catchError} from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import {ajax} from "rxjs/ajax";
import {Observable, of} from 'rxjs';
import {UserViewSettings, UserViewSettingsProp} from "../../models/common/ViewSettings";

import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";

import {ApiReturnType} from "../../models/common/ApiReturnType";
import {downloadFileHelper} from "../../helpers/FileHelpers";
import {CustomerRevenueReport} from "../../models/reporting/CustomerRevenueReport";
import {CustomerRevenueReportModule} from "../../models/reporting/CustomerRevenueReportModule";
import {ExportTypes} from "../../models/common/Export";
import {FileData} from "../../models/common/FileData";

export type CustomerRevenueReportActionTypes = FetchCustomerRevenueReportActionTypes

export interface FetchCustomerRevenueReportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_CUSTOMER_REVENUE_REPORT> {

}
export interface FetchCustomerRevenueReportSuccessAction extends ReduxAction<typeof FETCH_CUSTOMER_REVENUE_REPORT_SUCCESS> {
    reports: ApiReturnType<CustomerRevenueReport[]>
}
export interface FetchCustomerRevenueReportErrorAction extends ErrorAction<typeof FETCH_CUSTOMER_REVENUE_REPORT_ERROR> {
}

export type FetchCustomerRevenueReportActionTypes = FetchCustomerRevenueReportAction
    | FetchCustomerRevenueReportSuccessAction
    | FetchCustomerRevenueReportErrorAction


export const FETCH_CUSTOMER_REVENUE_REPORT = "FETCH_CUSTOMER_REVENUE_REPORT";
export const FETCH_CUSTOMER_REVENUE_REPORT_SUCCESS = "FETCH_CUSTOMER_REVENUE_REPORT_SUCCESS";
export const FETCH_CUSTOMER_REVENUE_REPORT_ERROR = "FETCH_CUSTOMER_REVENUE_REPORT_ERROR";

export function fetchCustomerRevenueReport(userViewSettings: UserViewSettings): FetchCustomerRevenueReportAction {
    return {type: FETCH_CUSTOMER_REVENUE_REPORT, userViewSettings};
}
export function fetchCustomerRevenueReportSuccess(reports: ApiReturnType<CustomerRevenueReport[]>): FetchCustomerRevenueReportSuccessAction {
    return {type: FETCH_CUSTOMER_REVENUE_REPORT_SUCCESS, reports};
}
export function fetchCustomerRevenueReportError(error: Error): FetchCustomerRevenueReportErrorAction {
    return {type: FETCH_CUSTOMER_REVENUE_REPORT_ERROR, error};
}

export function fetchCustomerRevenueReportEpic(
    action$: Observable<FetchCustomerRevenueReportAction>,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType(FETCH_CUSTOMER_REVENUE_REPORT),
        switchMap(action => {
            const settingPath = getUrlParamsFromViewSettings(action.userViewSettings);
            const path = `${APP_CONFIG.baseWeb}reporting/customer-revenue${settingPath}`;
            const language = currentLanguageFromStore(store$);

            return ajax(APP_CONFIG.getRequestObject(path)).pipe(
                map(response => fetchCustomerRevenueReportSuccess(
                    response.response as ApiReturnType<CustomerRevenueReport[]>
                )),
                catchError(error => of(
                    fetchCustomerRevenueReportError(error),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            language,
                            error.status,
                            CustomerRevenueReportModule.identifier,
                            "fetchCustomerRevenueReport"
                        )
                    )
                ))
            )
        })
    );
}


/**
 * export
 */
export interface FetchCustomerRevenueReportExportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_CUSTOMER_REVENUE_REPORT_EXPORT> {
    fileData: FileData
    exportTypes: ExportTypes
}
export interface FetchCustomerRevenueReportExportSuccessAction extends ReduxAction<typeof FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_SUCCESS> {
}
export interface FetchCustomerRevenueReportExportErrorAction extends ErrorAction<typeof FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_ERROR> {
}

export type FetchCustomerRevenueReportExportActionTypes = FetchCustomerRevenueReportExportAction
    | FetchCustomerRevenueReportExportSuccessAction
    | FetchCustomerRevenueReportExportErrorAction

export const FETCH_CUSTOMER_REVENUE_REPORT_EXPORT = "FETCH_CUSTOMER_REVENUE_REPORT_EXPORT";
export const FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_SUCCESS = "FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_SUCCESS";
export const FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_ERROR = "FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_ERROR";

export function fetchCustomerRevenueReportExport(
    exportTypes: ExportTypes,
    userViewSettings: UserViewSettings,
    fileData: FileData
): FetchCustomerRevenueReportExportAction {
    return {
        type: FETCH_CUSTOMER_REVENUE_REPORT_EXPORT,
        userViewSettings,
        exportTypes,
        fileData
    };
}
export function fetchCustomerRevenueReportExportSuccess(): FetchCustomerRevenueReportExportSuccessAction {
    return {type: FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_SUCCESS};
}
export function fetchCustomerRevenueReportExportError(
    error: Error
): FetchCustomerRevenueReportExportErrorAction {
    return {type: FETCH_CUSTOMER_REVENUE_REPORT_EXPORT_ERROR, error};
}

export function fetchCustomerRevenueReportExportEpic(
    action$: Observable<FetchCustomerRevenueReportExportAction>,
    store$: StateObservable<AppState>
) {
    return action$.pipe(
        ofType(FETCH_CUSTOMER_REVENUE_REPORT_EXPORT),
        switchMap(action => {
            const settingPath = getUrlParamsFromViewSettings(action.userViewSettings);
            const path = `${APP_CONFIG.baseWeb}reporting/customer-revenue/export/${action.exportTypes}${settingPath}`;
            const language = currentLanguageFromStore(store$);

            return ajax({
                ...APP_CONFIG.getRequestObject(path),
                responseType: "blob",
                async: false
            }).pipe(
                map(response => {
                    //@ts-ignore
                    downloadFileHelper(response.response, action.fileData);

                    return fetchCustomerRevenueReportExportSuccess();
                }),
                catchError(error => {

                    return of(
                        fetchCustomerRevenueReportExportError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            createErrorResponse(
                                language,
                                error.status ?? 200,
                                "",
                                "fetchCustomerRevenueReportExport"
                            )
                        )
                    )
                })
            )
        })
    );
}
